import { Row } from "@tanstack/react-table";
import {
  Archive,
  ArchiveX,
  CheckSquare,
  LucideIcon,
  PenLine,
  Star,
  Wrench,
} from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useProjectSession } from "../hooks/useProjectSession";

import { useProjectColumns } from "./columns";

import {
  CustomTable,
  FilterConfig,
} from "@/components/reusables/data-table/CustomTable";
import { REQUEST_METHODS } from "@/constants/global-constants";
import { PROJECT_CONSTANTS } from "@/constants/projects.constant";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { SpecialRateLocalKeys } from "@/constants/special-rate.constants";
import { ResponsePagination } from "@/hooks/usePagination";
import useQueryID from "@/hooks/useQueryId";
import { ICustomer } from "@/interfaces/customer.interface";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { Permissions } from "@/interfaces/globals";
import { IProject, ProjectStatus } from "@/interfaces/project.interface";
import { formatToCapitalizedWords } from "@/lib/utils";
import { useGetAllCustomerByWSQuery } from "@/redux/api/clientQuery";
import { useAlterProjectMutation } from "@/redux/api/projectsQuery";
import { hasWritePermission } from "@/redux/slices/permissionSlice";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

interface ProjectsTableProps {
  data: IProject[];
  toggleModal: () => void;
  isOngoing?: boolean;
  sheetToggle: () => void;
  pagination: ResponsePagination;
  toggleSpecialRateModal: () => void;
  setIsUpdateModalOpen: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      id: string;
    }>
  >;
}

export const ProjectsTable = ({
  data,
  setIsUpdateModalOpen,
  isOngoing,
  toggleModal,
  sheetToggle,
  pagination,
  toggleSpecialRateModal,
}: ProjectsTableProps): React.JSX.Element => {
  const { id: rowId, set } = useQueryID("projectId");

  const navigate = useNavigate();

  const { setProjectData } = useProjectSession();
  const handleRowClick = (id: string): void => {
    const singleData = data.find((d) => d.id === id);
    if (!singleData) {
      return;
    }

    const projectName = singleData.name || "";
    const clientName = singleData.customer?.name || "";

    setProjectData({
      projectName,
      clientName,
      projectId: id,
      customerId: singleData.customerId,
      status: singleData.status,
    });
    if (!rowId) {
      set(id);
      sheetToggle();
    }
  };

  const [alterProject] = useAlterProjectMutation();

  const handleStatusChange = async (
    id: string,
    status: ProjectStatus,
    method?: REQUEST_METHODS,
    params?: string,
  ): Promise<void> => {
    const isArchive = status === ProjectStatus.ARCHIVE;

    const response = await alterProject({
      projectId: id,
      data: { status },
      method: isArchive
        ? REQUEST_METHODS.PATCH
        : (method ?? REQUEST_METHODS.PUT),
      params: isArchive ? "archive" : params || "",
    });
    validateResponse({
      response,
      onSuccess: () => {
        customToast.success(PROJECT_CONSTANTS.MESSAGES.STATUS_UPDATED);
      },
    });
  };

  const writePermission = useSelector(hasWritePermission(Permissions.PROJECTS));

  const getStatus = (status?: ProjectStatus): ProjectStatus => {
    switch (status) {
      case ProjectStatus.COMPLETED:
        return ProjectStatus.ARCHIVE;
      case ProjectStatus.ONGOING:
        return ProjectStatus.COMPLETED;
      case ProjectStatus.ARCHIVE:
        return ProjectStatus.COMPLETED;
      default:
        return ProjectStatus.ONGOING;
    }
  };

  const getIcon = (status: ProjectStatus): LucideIcon => {
    switch (getStatus(status)) {
      case ProjectStatus.COMPLETED:
        return CheckSquare;
      case ProjectStatus.ARCHIVE:
        return Archive;
      default:
        return ArchiveX;
    }
  };

  const getActionOptions = (row: Row<IProject>): ActionButtonDataTypes[] => {
    const { id, status } = row.original;
    const statusName = formatToCapitalizedWords(getStatus(status));

    const defaultActions = [
      {
        icon: PenLine,
        name: "Edit Project",
        onclick: (): void => {
          setIsUpdateModalOpen((prev) => ({ isOpen: !prev.isOpen, id }));
        },
      },
      {
        icon: getIcon(status),
        name: statusName,
        onclick: async (): Promise<void> => {
          await handleStatusChange(id, getStatus(status));
        },
      },
    ];

    if (isOngoing) {
      return [
        ...defaultActions,
        {
          icon: ArchiveX,
          name: `Mark as Archive`,
          onclick: async (): Promise<void> => {
            await handleStatusChange(
              id,
              status,
              REQUEST_METHODS.PATCH,
              "archive",
            );
          },
        },
        {
          icon: Star,
          name: `Special Rates`,
          onclick: (): void => {
            set(id);
            toggleSpecialRateModal();
            localStorage.setItem(SpecialRateLocalKeys.PROJECT, id);
          },
        },
        {
          icon: Wrench,
          name: `Tools`,
          onclick: (): void => {
            navigate(ROUTES.PROJECTS + "/" + row.original.id);
          },
        },
      ];
    }

    if (!isOngoing && status === ProjectStatus.COMPLETED) {
      return defaultActions.slice(0, 2);
    }

    return [
      {
        icon: getIcon(status),
        name: `Unarchive`,
        onclick: async (): Promise<void> => {
          await handleStatusChange(
            id,
            getStatus(status),
            REQUEST_METHODS.PATCH,
            "unarchive",
          );
        },
      },
    ];
  };

  const columns = useProjectColumns({
    writePermission,
    getActionOptions,
  });

  const getTitle = (): string => {
    if (isOngoing === undefined) {
      return "Archived Projects";
    }
    if (isOngoing) {
      return "Ongoing Projects";
    }

    return "Completed Projects";
  };

  return (
    <CustomTable
      title={getTitle()}
      data={data}
      columns={columns}
      empty={{
        subtitle: "Add a new project to the list.",
      }}
      functions={{
        search: getTitle(),
        isExport: true,
        columnToggle: true,
        add: isOngoing && {
          label: "Add New Project",
          handleClick: toggleModal,
        },

        RightClickData: (
          row: GlobalRowDataTypes<IProject>,
        ): ActionButtonDataTypes[] => getActionOptions(row as Row<IProject>),
        onRowClick: handleRowClick,
        pagination,
      }}
      filters={{
        customerId: {
          isPaginatedSingleSelect: true,
          label: "Customer",
          paginatedSelectConfig: {
            useQuery: useGetAllCustomerByWSQuery,
            baseQuery: { status: "ACTIVE" },
            renderConfig: {
              valueKey: "id",
              labelKey: "name",
            },
          },
        } satisfies FilterConfig<ICustomer, { status: string }>,
      }}
      hasWritePermission={writePermission}
      hide={{
        functions: true,
        filters: true,
      }}
    />
  );
};
